/* tipos.ts — la forma de lo que devuelve la API.
 *
 * Escritos a mano contra el código real (`app/server.py`, `app/oportunidades.py`,
 * `app/auth/routes.py`, `olap/build_procesos_abiertos.py`), no generados: la API no publica
 * un esquema OpenAPI útil porque casi todo se devuelve como `JSONResponse` de diccionarios.
 *
 * Regla al ampliarlos: si un campo puede faltar en el snapshot vigente, va OPCIONAL. El cubo se
 * reconstruye por las noches y las capas nuevas del enrich (duración, lotes, ejecución real)
 * aparecen antes en el código que en los datos publicados.
 */

/* ── identidad y permisos ─────────────────────────────────────────────────── */

/** Capacidades de una cuenta. Ocultar en la interfaz es COMODIDAD, no seguridad:
 *  cada endpoint verifica por su cuenta. */
export type Capacidad = 'panel' | 'chat' | 'paa' | 'analizar' | 'rup' | 'perfil_nit'

export interface Alcance {
  id: number
  nombre: string
  descripcion: string
}

export interface Yo {
  usuario: string
  nombre: string
  es_admin: boolean
  caps: Partial<Record<Capacidad, boolean>>
  /** Con alcance asignado la cuenta solo ve una porción del universo. */
  alcance: Alcance | null
  /** Clave temporal sin cambiar: el servidor bloquea todo lo demás hasta que se cambie. */
  cambiar_clave: boolean
  /** Testigo anti-CSRF de la sesión. Lo exige `POST /api/cuenta/clave`. */
  csrf: string
}

/* ── procesos ─────────────────────────────────────────────────────────────── */

export interface Proceso {
  id: string
  referencia: string
  /** Ojo: en esta fuente `objeto` suele traer solo el código de referencia; el texto real de
   *  qué se contrata vive en `descripcion`. Al mostrar, preferir `descripcion`. */
  objeto: string
  descripcion: string
  entidad_nit: string
  modalidad: string
  tipo_contrato: string
  sector: string
  unspsc: string
  precio_base: number | null
  fecha_publicacion: string
  fecha_cierre: string
  fecha_apertura: string | null
  url: string
  /** Días para preparar la oferta. NO es la duración del contrato: son cosas distintas y la
   *  ficha debe mantenerlas distinguibles. */
  dias_plazo: number | null
  plazo_expres: boolean
  plazo_mediana_modalidad: number | null
  oferentes_esperados: number | null
  oferentes_esperados_n: number | null
  oferentes_esperados_fuente: string | null
  p_oferente_unico: number | null
  visualizaciones: number | null
  fecha_modificacion_oficial: string | null
  nuevo: boolean
  /**
   * Qué cambió del proceso desde la corrida anterior del sync. ⚠ Estaba declarado `string[]` y NO
   * lo es: `build_procesos_abiertos.py` emite objetos `{campo, antes, ahora}`. Nadie lo consumía,
   * así que la mentira no se notó — y eso es exactamente lo que pasa con un tipo que nada verifica.
   *
   * Es un DIFF ENTRE SNAPSHOTS, no una adenda: dice que el cierre se movió, no quién lo movió.
   * Quien lo muestre no puede atribuirlo a una adenda concreta.
   */
  cambios: CambioProceso[] | null
  pop: number | null

  /** Capas del enrich que pueden no estar en el snapshot vigente. */
  duracion?: number | null
  unidad_duracion?: string | null
  numero_lotes?: number | null

  /** Sembrados por el servidor al buscar y al ver la ficha. El frontend NO los recalcula. */
  /** Puntaje crudo, SIN acotar: ordena. Puede pasar de 130 o ser negativo — no se muestra. */
  _score?: number
  /**
   * El puntaje como se le enseña a la empresa: entero de 0 a 100, el «N /100» y el ancho de la
   * barra. NULO cuando no hay perfil con qué comparar — sin él, `_score` es solo un orden general
   * y llamarlo afinidad sería prometer algo que nadie ha comprobado.
   */
  _afinidad?: number | null
  /** Banda e5 de procesos parecidos (ganador <95 % del techo). Solo si el precio es palanca. */
  _analogos?: { n: number; p25_pct: number; p50_pct: number; p75_pct: number }
  _banda?: BandaVeredicto | null
  _sem?: number | null
  _sem_fuente?: string | null

  /** PARA QUIÉN sirve este proceso. Solo en modo cartera: con una empresa sola la respuesta es
   *  obvia y repetirla en cada fila sería ruido. Es la empresa GANADORA — la de mejor afinidad —,
   *  y `_afinidad`, `_banda` y los criterios hablan de ella. */
  _para?: { empresa_id: number; razon_social: string }

  /** Solo en la ficha. */
  documentos?: Documento[]
}

/** Un campo del proceso que cambió entre dos corridas del sync. */
export interface CambioProceso {
  campo: 'fecha_cierre' | 'precio_base' | string
  antes: string | number | null
  ahora: string | number | null
}

export interface Documento {
  nombre: string
  categoria: string
  grupo: string
  fecha: string | null
  url: string
}

export interface Entidad {
  nombre: string
  departamento: string
  ciudad: string
  orden: string
  riesgo: Record<string, unknown>
  /** Lo adjunta `oportunidades._entidades()` al leer, no el build. */
  apertura: Apertura | null
  /** Departamentos donde la entidad EJECUTA de verdad, no solo su sede. */
  ejecuta_en?: { dep: string; pct: number }[]
  dane_dep?: string | null
}

/** Índice de apertura del comprador: 0-100 donde ALTO ES BUENO (reparte y hay competencia
 *  real). Es el inverso del puntaje forense y no el mismo número con otro nombre. */
export interface Apertura {
  score_0_100: number
  banda: 'cerrado' | 'medio' | 'abierto' | 'sin_datos'
}

/* ── veredicto ────────────────────────────────────────────────────────────── */

/** Bandas de la afinidad. Los cortes viven en `BANDAS_VEREDICTO` (app/oportunidades.py) y este
 *  tipo solo nombra el resultado: el frontend NO recalcula el puntaje. */
export type BandaVeredicto = 'compite' | 'cautela' | 'piensalo' | 'mejor_no'

/**
 * Un criterio de la afinidad. El color nunca informa solo: `texto` es obligatorio.
 *
 * Cuatro signos y no tres, porque «no hay dato» y «ojo con esto» son cosas distintas:
 *   ok        cumple
 *   no        no cumple
 *   alerta    cumple o no aplica, pero hay algo que mirar antes de invertir horas
 *             («se divide en 3 lotes», «su proveedor habitual concentra el 58% del sector»)
 *   sin_dato  no se puede evaluar; no cuenta ni a favor ni en contra
 */
export interface Criterio {
  signo: 'ok' | 'no' | 'alerta' | 'sin_dato'
  texto: string
}

/** El veredicto de un proceso para un perfil dado, tal como lo devuelve el servidor. */
export interface Veredicto {
  id: string
  /** Crudo, sin acotar: solo sirve para comparar dos veredictos entre sí. */
  score: number
  /** 0-100, lo que se muestra. Nulo si no hay perfil con qué comparar. */
  afinidad: number | null
  banda: BandaVeredicto | null
  /** Una frase que resume el veredicto nombrando lo que pesó. */
  prosa: string
  /** «¿Se ajusta a tu empresa?», criterio por criterio. Vacío si no hay perfil que comparar. */
  criterios: Criterio[]
  /** «Lo que decide»: las cifras que cambian la decisión, cada una con su procedencia. */
  claves: Clave[]
}

export interface Clave {
  clave: string
  valor: string
  nota: string
  /** De cuántos casos sale. Un promedio sin su n no se puede pesar. */
  fuente: string
}

/**
 * Respuesta de `POST /api/oportunidades/{id}/simular`: cómo quedaría el veredicto con un perfil
 * distinto, sin guardar nada. Se devuelven los DOS lados porque la ficha enseña el cambio
 * («46 → 18»), no el resultado.
 */
export interface Simulacion {
  antes: Veredicto
  despues: Veredicto
  /** El perfil que se guardaría si se confirma, ya con el delta aplicado. */
  perfil: Perfil
}

/**
 * El riel de la ficha (fase 5) separa «A favor» de «En contra». No hace falta que el servidor
 * mande dos listas: se parten de `criterios` por su `signo`, que es la misma información sin la
 * posibilidad de que las dos versiones se desincronicen.
 */
export function partirCriterios(criterios: Criterio[]) {
  return {
    aFavor: criterios.filter((c) => c.signo === 'ok'),
    // Las advertencias y los «sin dato» van con lo que hay que mirar, no con lo que respalda: el
    // riel del diseño solo tiene dos columnas, y meterlas a favor sería contarlas como puntos.
    contra: criterios.filter((c) => c.signo !== 'ok'),
  }
}

/* ── búsqueda ─────────────────────────────────────────────────────────────── */

export interface FiltrosBusqueda {
  texto?: string
  departamento?: string
  sector?: string
  modalidad?: string
  valor_min?: number | null
  valor_max?: number | null
  dias_min?: number | null
  dias_max?: number | null
  apertura?: string
  solo_nuevos?: boolean
  solo_cambios?: boolean
  solo_mio?: boolean
  /** Filtro duro por clasificación RUP. Apagado por omisión: esconde 22-40 % de victorias. */
  solo_unspsc?: boolean
  lista?: 'activos' | 'siguiendo' | 'descartados'
  orden?: 'rel' | 'plata' | 'apertura' | 'pub' | 'cierre'
  offset?: number
  limit?: number
}

/** Lo que acompaña siempre a una lista o a una ficha: los procesos no se entienden solos. */
export interface Contexto {
  meta: Meta
  entidades: Record<string, Entidad>
  top_proveedores: Record<string, Proveedor[]>
  lideres_sector: Record<string, Proveedor[]>
  banda_precios: Record<string, BandaPrecios>
  dispersion_ofertas: Record<string, DispersionOfertas>
  gasto_sector: Record<string, unknown>
  ut_integrantes: Record<string, unknown>
}

/* ── el plan de lectura del pliego (7b) ───────────────────────────────────── */

/**
 * Un documento candidato a que lo lea el análisis.
 *
 * Los tres estados son cosas distintas y la pantalla tiene que distinguirlas:
 *   · `listo`      ya extraído en el corpus: se lee al instante y no cuesta descarga.
 *   · `por_leer`   hay que descargarlo. Es lo que hace que la primera corrida tarde.
 *   · `sin_texto`  NO se puede elegir. Se enseña igual, con su motivo: «no aparece» y «aparece y
 *                  no se puede» son mensajes muy distintos para quien busca su pliego.
 */
export interface DocumentoPlan {
  nombre: string
  categoria: string
  url: string
  paginas: number
  /** Cómo se extrajo, si ya está en el corpus. `ocr` merece advertencia: las cifras leídas por
   *  reconocimiento óptico conviene confirmarlas contra el documento. */
  metodo: string
  estado: 'listo' | 'por_leer' | 'sin_texto'
  motivo: string
  sugerido: boolean
}

export interface PlanPliego {
  /** Cuántos documentos lee UNA corrida. El tope lo pone el servidor, no el cliente. */
  tope: number
  del_corpus: boolean
  documentos: DocumentoPlan[]
}

export interface RespuestaBusqueda extends Contexto {
  procesos: Proceso[]
  /** Cuántos pasan los FILTROS. ⚠ No depende del perfil: el perfil cambia el orden, no quién entra.
   *  Para «cuántos le sirven a esta empresa» está `conteo_bandas`. */
  total: number
  /**
   * Cuántos caen en cada banda de veredicto, sobre el resultado completo y no sobre la página.
   *
   * Es lo ÚNICO de esta respuesta que depende del perfil, y por eso existe: presentar `total` como
   * «le sirven hoy» daría la misma cifra para dos empresas con perfiles opuestos.
   */
  conteo_bandas?: Record<BandaVeredicto | 'sin_afinidad', number>
  /**
   * Cuántos cierran dentro de la semana, sobre el resultado completo.
   *
   * Lo manda el servidor y no se calcula aquí a propósito: el navegador solo tiene el LOTE cargado
   * —24 de 68—, así que contarlo en el cliente daba un número a la baja presentado como si fuera
   * del total, sin nada que delatara la diferencia.
   */
  cierran_pronto?: number
  /**
   * Cuántos escondieron las REGLAS DEL PERFIL («no me muestres pagos a más de 90 días»).
   *
   * No son los filtros de la barra, que se ven puestos en la pantalla: son reglas configuradas
   * otro día y en otra pantalla, que sin esta cifra esconden procesos sin dejar rastro. Decir
   * cuántos son es lo que las hace reversibles en vez de invisibles.
   */
  bloqueados_por_regla?: number
  /** Solo en modo cartera: cuántas empresas se puntuaron de verdad y cuántas se pidieron. Si el
   *  tope dejó alguna fuera, la pantalla tiene que decirlo. */
  cartera?: { puntuadas: number; pedidas: number; tope: number }
  presupuesto_total: number
  offset: number
  has_more: boolean
}

export interface RespuestaFicha extends Contexto {
  procesos: [Proceso]
  /** El veredicto viaja CON la ficha, no en una llamada aparte: el rail es lo primero que se lee,
   *  y dos peticiones serían dos oportunidades de que la pantalla se vea medio pintada. */
  veredicto: Veredicto
  /** Qué se sabe ya del pliego, sin abrir el informe. `null` = nadie lo ha leído todavía.
   *  Viaja con la ficha por lo mismo que el veredicto: el rail decide qué botón ofrecer, y
   *  pedirlo aparte haría que «Analizar el pliego» parpadeara a «Ver el análisis». */
  analisis_previo: AnalisisPrevio | null
}

export interface Proveedor {
  nit: string
  nombre: string
  n?: number
  gasto?: number
  sancionado?: boolean
}

export interface BandaPrecios {
  mediana_pct: number
  pct_al_tope: number
  p25_pct?: number
  p75_pct?: number
  n?: number
}

/** Cuánto se separan entre sí las ofertas de un mismo proceso. Si la brecha es de décimas, la
 *  aritmética de la propuesta económica decide más que la estrategia. */
export interface DispersionOfertas {
  brecha_mediana_pct?: number
  cv_mediano_pct?: number
  n?: number
}

export interface Meta {
  fecha_corte_grafo?: string
  sectores?: string[]
  departamentos?: string[]
  modalidades?: string[]
  dane_departamentos?: Record<string, string>
  base_modalidad?: Record<string, { adj_expres?: number }>
  apertura?: {
    cortes: Record<string, number>
    definiciones: Record<string, string>
  }
  [k: string]: unknown
}

/* ── perfil de la empresa ─────────────────────────────────────────────────── */

export interface Perfil {
  nit: string
  razon_social: string
  sectores: string[]
  deps: string[]
  /** En millones de pesos, como los escribe la persona. */
  min: number | null
  max: number | null
  /** ¿La empresa tiene RUP vigente? Hay modalidades que lo exigen. */
  rup: boolean
  unspsc: string[]
  /** Lo que la empresa hace y no aparece en SECOP; se alimenta de las correcciones de la ficha. */
  temas_manuales: string[]
  contratos_a_la_vez: number | null
  /** Días mínimos de plazo que necesita para armar una oferta. */
  dias_min_oferta: number | null
  fuera_territorio: boolean
  /**
   * ¿Esta empresa entra en el modo «Todas» de la bandeja?
   *
   * Vive en el perfil —o sea, EN LA CUENTA— y no en la sesión ni en el navegador: la cartera es de
   * la cuenta y el triaje de la mañana tiene que ser el mismo mañana y desde otra máquina.
   * AUSENTE = sí, que es lo que espera quien nunca ha tocado el ajuste.
   */
  comparar?: boolean
  /**
   * ¿Ya pasó por los primeros pasos? Lo pone el onboarding tanto si se completa como si se salta.
   *
   * Vive en el PERFIL —o sea, en la cuenta— y no en el navegador: quien lo saltó en el portátil no
   * tiene que volver a saltarlo en la oficina.
   */
  primeros_pasos?: boolean
  reglas_no_ver: ReglaNoVer[]
  /** Conteo de descartes por motivo: de aquí salen las reglas sugeridas. */
  descartes_agg: Record<string, number>
  avisos: Avisos
  /**
   * Seguidos y descartados, por id de proceso. Va en el perfil —y no en el navegador, como en el
   * panel anterior— porque si no, «siguiendo · 3» dependía de desde dónde miraras.
   */
  watch: Record<string, MarcaProceso>
}

export type ResultadoOferta = (typeof RESULTADOS_OFERTA)[number]['clave']

/** Vocabulario CERRADO del desenlace. Sobrevive a la poda del watch: es la etiqueta
 *  que no se puede reconstruir cuando el proceso ya salió del cubo. */
export const RESULTADOS_OFERTA = [
  { clave: 'presente', etiqueta: 'Presenté oferta' },
  { clave: 'gano', etiqueta: 'Nos adjudicaron' },
  { clave: 'perdio', etiqueta: 'No nos adjudicaron' },
  { clave: 'fuera', etiqueta: 'Quedamos por fuera' },
] as const

export interface MarcaProceso {
  estado: 'sigo' | 'desc'
  /** Desenlace de la oferta, si lo declaró. No se poda con el cierre. */
  resultado?: ResultadoOferta
  /** Solo al descartar. Alimenta «Lo que no quieres ver» del perfil. */
  motivo?: string
  /**
   * Lo que el asistente respondió sobre este proceso y se guardó desde ahí («Guardar en la ficha»).
   * Vive en la marca y no en un almacén aparte porque es exactamente lo mismo: una anotación de la
   * persona sobre UN proceso, que se poda con él cuando el proceso cierra.
   */
  nota?: string
  ts?: number
}

/** Los motivos de descarte, cerrados a propósito: de su recuento salen las reglas sugeridas, y con
 *  texto libre no habría nada que contar. */
export const MOTIVOS_DESCARTE = [
  'No cumplo requisitos',
  'El comprador no me convence',
  'Plazo insuficiente',
  'No me interesa',
  'Otro',
] as const

/**
 * Las tres reglas de «Lo que no quieres ver». El vocabulario es CERRADO y lo comparte el servidor
 * (`REGLAS_NO_VER` en `app/oportunidades.py`, con su prueba): una clave que el servidor no sabe
 * aplicar sería una casilla que se marca y no hace nada, justo en la única pantalla donde alguien
 * pide explícitamente dejar de ver algo.
 *
 * Dos ESCONDEN y una AVISA. `pago_lento` no esconde porque decidir que ningún contrato de un
 * pagador lento le sirve a la empresa depende de su caja, no del historial del comprador.
 */
export type ClaveRegla = 'presupuesto_min' | 'plazo_corto' | 'pago_lento'

export const REGLAS: {
  clave: ClaveRegla
  /** El motivo de descarte del que nace la sugerencia. */
  motivo: (typeof MOTIVOS_DESCARTE)[number]
  porOmision: number
  unidad: 'millones' | 'días'
  oculta: boolean
  texto: (valor: number) => string
}[] = [
  {
    clave: 'presupuesto_min',
    motivo: 'No me interesa',
    porOmision: 100,
    unidad: 'millones',
    oculta: true,
    texto: (v) => `Ocultar procesos por debajo de $${v} millones`,
  },
  {
    clave: 'plazo_corto',
    motivo: 'Plazo insuficiente',
    porOmision: 4,
    unidad: 'días',
    oculta: true,
    texto: (v) => `Ocultar procesos con menos de ${v} días de plazo`,
  },
  {
    clave: 'pago_lento',
    motivo: 'El comprador no me convence',
    porOmision: 90,
    unidad: 'días',
    oculta: false,
    texto: (v) => `Avisarme cuando la entidad tarde más de ${v} días en pagar`,
  },
]

export interface ReglaNoVer {
  clave: ClaveRegla
  activa: boolean
  /** Umbral de la regla: millones, días… según la clave. */
  valor: number | null
}

export interface Avisos {
  frecuencia: 'diario' | 'semanal' | 'app'
  umbral_afinidad: number
  /**
   * El nombre anterior del mismo umbral, de cuando la cifra se llamaba «encaje». Se conserva SOLO
   * para leerlo: los perfiles guardados antes del cambio de vocabulario lo traen así, y sin esto
   * quien tuviera puesto un 85 se lo encontraría en 70 sin que nada lo avisara. Se lee, se copia a
   * `umbral_afinidad` y no se vuelve a escribir.
   */
  umbral_encaje?: number
  cambios_en_seguidos: boolean
}

/* ── la cartera de empresas ───────────────────────────────────────────────── */

/**
 * Una empresa de la cartera, tal como la lista `GET /api/cartera`.
 *
 * ⚠ SIN el perfil y SIN «completitud», a propósito: qué le falta a un perfil lo define el cliente
 * (`contarFaltantes`, con su prueba), y calcularlo también en el servidor serían dos definiciones
 * de lo mismo desincronizándose en silencio. Lo que sí viajan son dos HECHOS crudos, que no son una
 * definición de nada y evitan traerse veinticinco perfiles enteros para pintar una lista.
 */
export interface EmpresaCartera {
  id: number
  nit: string
  razon_social: string
  version: number
  creada_en: number
  actualizado_en: number
  tiene_rup: boolean
  tiene_perfil: boolean
}

export interface Cartera {
  empresas: EmpresaCartera[]
  /** Cuál está activa EN ESTA SESIÓN. Otro navegador de la misma cuenta puede tener otra. */
  activa_id: number | null
  /** Tope de empresas por cuenta (`TOPE_CARTERA` en `app/auth/store.py`). */
  tope: number
}

export interface RespuestaPerfil {
  perfil: Perfil | null
  /** El destilado del RUP: lo que devuelve `POST /api/rup`. */
  rup: RupDestilado | null
  version: number
  actualizado_en: number | null
  /**
   * Cuál de las empresas de la cartera es este perfil. `null` si la cuenta no tiene ninguna.
   *
   * ⚠ Hay que DEVOLVERLO en cada `PUT /api/perfil`. Una cuenta puede llevar varias empresas y cuál
   * está activa vive en la SESIÓN, que las pestañas comparten: si otra pestaña cambia de empresa
   * mientras esta edita, un guardado «sobre la activa» acabaría en la empresa equivocada. El
   * servidor responde 409 cuando el `empresa_id` que se manda no es el activo — pero solo puede
   * hacerlo si se le manda. Sin este campo la guardia queda pasiva y el fallo vuelve, en silencio.
   */
  empresa_id: number | null
}

export interface RupDestilado {
  empresa: string
  nit: string
  resumen: string
  unspsc: string[]
  sectores: string[]
  capacidad_max_pesos: number | null
  perfil: {
    tamano_empresa?: string
    municipio?: string
    departamento?: string
    liquidez?: number
    endeudamiento?: number
    /** Los tres que faltaban para poder cruzar la capacidad FINANCIERA y la ORGANIZACIONAL que
     *  exigen los pliegos. Salen de los rótulos del certificado (`POST /api/rup`); si el
     *  certificado no los trae, el requisito sale «sin dato» y no cuenta en contra. */
    cobertura_intereses?: number
    rentabilidad_activos?: number
    rentabilidad_patrimonio?: number
    capital_trabajo?: number
    patrimonio?: number
    /** Estados financieros de Supersociedades, vía el historial en SECOP (`graph.perfil_secop`).
     *  Es el respaldo cuando no hay certificado RUP cargado, y de aquí se DERIVAN la rentabilidad
     *  sobre activos y sobre patrimonio. Cobertura: solo sociedades comerciales vigiladas — que no
     *  esté NO significa debilidad financiera.
     *
     *  Estaba sin declarar aunque `app/veredicto.py` lo lee desde ago-2026: el contrato TS↔Python
     *  tenía un agujero justo en los campos que deciden si una empresa queda habilitada. */
    capacidad_financiera?: {
      anio?: number
      patrimonio?: number
      liquidez?: number
      /** En PORCENTAJE (0-100), no en razón: `veredicto._financiera` lo divide entre 100 antes de
       *  compararlo con el «≤ 0,7» del pliego. Compararlos sin convertir declararía incumplido a
       *  cualquiera, porque 45 nunca es ≤ 0,7. */
      endeudamiento_pct?: number
      capital_trabajo?: number
      activo_total?: number
      utilidad?: number
    } | null
    /** `total_smmlv`/`max_smmlv` vienen del RUP; `total_pesos` del historial en SECOP
     *  (`graph.perfil_secop`). Las dos fuentes llenan campos distintos del mismo objeto. */
    experiencia?: {
      /** `null` = NO SE MIDIÓ, y no es lo mismo que 0. El perfil que sale del registro mercantil
       *  (`fuente: 'rues'`) manda los tres en `null` a propósito: esa empresa no tiene historial en
       *  SECOP, lo que no dice nada de su trayectoria privada. `veredicto._comparar` bifurca justo
       *  aquí para responder «no sabemos» en vez de «no cumples». */
      n_contratos?: number | null
      total_smmlv?: number | null
      max_smmlv?: number | null
      max_pesos?: number | null
      total_pesos?: number | null
    }
    territorios?: string[]
  } | null
  fuente: 'rup' | 'secop' | 'rues'
}

/* ── asistente ────────────────────────────────────────────────────────────── */

/** Eventos del SSE de `/api/chat/stream`. Los emite `llm.run_turn` como diccionarios sueltos;
 *  esta unión es el contrato tal como está hoy en `app/llm.py`. */
export type EventoChat =
  | { type: 'status'; phase: string; model?: string }
  | { type: 'thought'; text: string }
  | { type: 'tool'; tool: string; args?: Record<string, unknown>; n?: number; error?: string }
  | { type: 'chart'; option: unknown; meta: { tipo?: string; titulo?: string; subtitulo?: string } }
  | { type: 'answer_delta'; text: string }
  | { type: 'answer_reset' }
  | { type: 'answer'; text: string }
  | { type: 'suggestions'; items: { q: string; tema: string }[] }

/* ── informe del pliego ───────────────────────────────────────────────────── */

/**
 * Eventos del SSE de `/api/analizar-proceso`, tal como los emite `app/analizar_proceso.py`.
 *
 * El orden importa y es deliberado: primero el PLAN de lectura (`documentos`), luego un `documento`
 * por cada uno según se lee, después `habilitantes` y `veredicto` —que dependen solo del JSON
 * extraído y ya se saben—, y al final el `informe`, que es la llamada larga. Hacer esperar un
 * minuto por algo que ya está calculado es hacer esperar por otra cosa.
 */
export type EventoPliego =
  | { type: 'paso'; texto: string }
  /** El plan de lectura completo, antes de empezar. Un indicador sin denominador no informa. */
  | { type: 'documentos'; items: DocumentoLeido[]; del_corpus: boolean; guardado?: boolean }
  /** Avance de UN documento del plan, por su índice. */
  | { type: 'documento'; i: number; estado: EstadoDocumento; paginas?: number; motivo?: string }
  /** Los habilitantes como JSON con cita VERBATIM. `descartados` son los que el servidor tumbó
   *  por no traer una cita que aparezca de verdad en el texto. */
  | { type: 'habilitantes'; campos: Record<string, CampoPliego>; descartados: number }
  | ({ type: 'veredicto' } & AjusteVeredicto)
  | {
      type: 'informe'
      markdown: string
      documentos_analizados: DocumentoLeido[]
      /** Solo cuando el informe viene de disco: cuándo se hizo y si el corpus ya tiene más. */
      analizado_en?: number
      desactualizado?: boolean
    }
  | { type: 'error'; texto: string }
  | { type: 'done' }

/** `ocr_en_curso`: no tenía capa de texto y se está rasterizando en segundo plano. No es un fallo
 *  —el documento llegará al corpus y el próximo análisis lo incluirá—, así que no se pinta como
 *  tal. `ilegible` es el caso en que NO va a llegar: sin tesseract, o formato que no sabemos leer. */
export type EstadoDocumento = 'cola' | 'leyendo' | 'leido' | 'ilegible' | 'ocr_en_curso'

export interface DocumentoLeido {
  nombre: string
  categoria: string
  url?: string
  paginas?: number
  estado?: EstadoDocumento
}

/** Un dato extraído del pliego. Sin `cita` no llega aquí: `pliego_estructura.validar` descarta en
 *  el servidor todo campo cuya cita no aparezca de verdad en el texto del documento. */
export interface CampoPliego {
  valor: unknown
  cita: string
}

/**
 * Cómo cambia el veredicto al leer el pliego. Lo calcula `app/veredicto.py::ajustar()`, que es una
 * función pura — el «46 → 18» es una afirmación sobre la afinidad, no una resta del navegador.
 */
export interface AjusteVeredicto {
  antes: { score: number; afinidad: number | null; banda: BandaVeredicto | null }
  despues: { score: number; afinidad: number; banda: BandaVeredicto }
  cambia: boolean
  requisitos: RequisitoPliego[]
  /** El requisito que decide, si hay uno. Es el titular de la caja. */
  decisivo: string | null
  prosa: string
}

export interface RequisitoPliego {
  clave: string
  etiqueta: string
  /** `sin_dato` = no había con qué compararlo en el perfil; NO cuenta en contra. */
  signo: 'ok' | 'no' | 'sin_dato'
  exigido: string
  tuyo: string
  cita: string
  /** Solo cuando no se cumple: qué haría falta, y si se arregla con papeles o no. */
  arreglo?: string
}

/**
 * El análisis ya hecho de un proceso, servido por `GET /api/analizar-proceso/guardado`.
 *
 * El informe y los habilitantes salen del corpus —una sola corrida de modelo por proceso,
 * compartida entre todas las empresas, porque no dependen de quién mira—; el `veredicto` se
 * recalcula en el servidor para la empresa activa en cada petición.
 */
export interface AnalisisGuardado {
  informe: string
  habilitantes: Record<string, CampoPliego>
  descartados: number
  documentos_analizados: DocumentoLeido[]
  /** Epoch en segundos. */
  analizado_en: number
  /** El corpus tiene documentos que no estaban cuando se analizó (una adenda, típicamente). */
  desactualizado: boolean
  veredicto: AjusteVeredicto | null
}

/**
 * Lo que la FICHA sabe del análisis sin cargar el informe entero. Viene dentro de
 * `GET /api/oportunidades/{id}` como `analisis_previo`; `null` = nadie ha leído el pliego.
 */
export interface AnalisisPrevio {
  analizado_en: number
  n_documentos: number
  desactualizado: boolean
  /** Cuántos requisitos habilitantes cumples, no cumples, y cuántos no se pudieron verificar. */
  cumple: { ok: number; no: number; sin_dato: number }
  /** Solo los que te dejan fuera. Es lo accionable. */
  incumplidos: { etiqueta: string; exigido: string; tuyo: string; arreglo?: string }[]
  /** Conteo de hallazgos por severidad, tal como la puso el modelo. */
  riesgos: { alta: number; media: number; baja: number }
  afinidad: number | null
  banda: BandaVeredicto | null
}

/** Severidad de un hallazgo de «Requisitos que podrían dejarte fuera». La emite el modelo como
 *  `[Alta]`/`[Media]`/`[Baja]` al principio de cada viñeta (ver el prompt de `analizar_proceso`). */
export type Severidad = 'alta' | 'media' | 'baja'
